// Tranche CC partial: site-wide emergency lockdown command page.
//
// The portal already exposes POST /api/v1/lockdown (engage) and POST
// /api/v1/lockdown/clear (release), both master-only via the
// MASTER_LOCKDOWN capability. Until this page landed, an operator had
// to curl those endpoints by hand — not acceptable for the worst-case
// "lock the building right now" scenario.
//
// IMPORTANT product fact: the backend refuses both endpoints with a 422
// `lockdown_not_live_verified` until emergency controls are matrix-
// flipped on the target controller. The page surfaces that as an inline
// warning so the operator understands why the buttons may fail without
// thinking it's a credentials or network problem.

import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Button, Card, CardBody, Dialog, Icon, Input, Label } from '@ng-unifi/ui';
import { apiClient, ApiError, newIdempotencyKey } from '../lib/api.js';

type ModalKind = 'engage' | 'clear';

interface ModalState {
  kind: ModalKind;
  reason: string;
  busy: boolean;
  /**
   * Operator must type CONFIRM_TEXT exactly to enable the destructive
   * submit. Prevents elbow-clicks on a critical-stakes button.
   */
  confirmText: string;
  /**
   * Backend error envelope. We treat `lockdown_not_live_verified` as a
   * distinct category so the UI explains it instead of looking like a
   * generic 5xx.
   */
  error: { message: string; notLiveVerified: boolean } | null;
  /**
   * One Idempotency-Key per modal open. Re-submits inside the same modal
   * collapse to the same operation server-side.
   */
  idempotencyKey: string;
}

const CONFIRM_TEXT: Record<ModalKind, string> = {
  engage: 'LOCKDOWN',
  clear: 'CLEAR',
};

/** POST /lockdown and /lockdown/clear both return the controller's request id. */
interface LockdownResponse {
  data?: { unifi_request_id?: string | null };
}

interface RecentLockdown {
  id: string;
  action: 'LOCKDOWN_ENGAGED' | 'LOCKDOWN_CLEARED';
  created_at: string;
  actor_user_id: string | null;
}

export function GlobalLockdownPage() {
  const [modal, setModal] = useState<ModalState | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [recent, setRecent] = useState<RecentLockdown[] | null>(null);
  const [recentError, setRecentError] = useState<string | null>(null);

  useEffect(() => {
    // Best-effort: pull the last few lockdown audit entries so the page
    // has SOME notion of current state. Two failure modes are both
    // acceptable — non-master callers will 403 (we don't render the
    // section), and audit unavailable yields an empty list.
    let cancelled = false;
    apiClient
      .get<{
        data: Array<{
          id: string;
          action: string;
          created_at: string;
          actor_user_id: string | null;
        }>;
      }>('/api/v1/audit?action=LOCKDOWN_ENGAGED&limit=5')
      .then((engaged) =>
        apiClient
          .get<{
            data: Array<{
              id: string;
              action: string;
              created_at: string;
              actor_user_id: string | null;
            }>;
          }>('/api/v1/audit?action=LOCKDOWN_CLEARED&limit=5')
          .then((cleared) => {
            if (cancelled) return;
            const merged: RecentLockdown[] = [
              ...engaged.data.map((r) => ({
                ...r,
                action: 'LOCKDOWN_ENGAGED' as const,
              })),
              ...cleared.data.map((r) => ({
                ...r,
                action: 'LOCKDOWN_CLEARED' as const,
              })),
            ]
              .sort((a, b) => b.created_at.localeCompare(a.created_at))
              .slice(0, 10);
            setRecent(merged);
          }),
      )
      .catch((err: unknown) => {
        // UX-019: this stored [] on failure, and the empty branch below then
        // told the operator "No lockdown events recorded on this install" — a
        // statement about the site derived from a request that did not answer.
        if (cancelled) return;
        setRecent([]);
        setRecentError(
          err instanceof ApiError ? err.message : 'Could not read the lockdown audit.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const lastEvent = (recent ?? [])[0] ?? null;
  const inferredState: 'engaged' | 'cleared' | 'unknown' =
    !lastEvent
      ? 'unknown'
      : lastEvent.action === 'LOCKDOWN_ENGAGED'
        ? 'engaged'
        : 'cleared';

  function openModal(kind: ModalKind): void {
    setModal({
      kind,
      reason: '',
      busy: false,
      confirmText: '',
      error: null,
      idempotencyKey: newIdempotencyKey(),
    });
  }

  async function submitModal(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (!modal) return;
    if (modal.confirmText !== CONFIRM_TEXT[modal.kind]) {
      setModal({
        ...modal,
        error: {
          message: `Type ${CONFIRM_TEXT[modal.kind]} exactly to confirm.`,
          notLiveVerified: false,
        },
      });
      return;
    }
    if (modal.kind === 'engage' && modal.reason.trim().length < 1) {
      setModal({
        ...modal,
        error: { message: 'Reason is required when engaging lockdown.', notLiveVerified: false },
      });
      return;
    }
    setModal({ ...modal, busy: true, error: null });
    try {
      let res: LockdownResponse;
      if (modal.kind === 'engage') {
        res = await apiClient.post<LockdownResponse>(
          '/api/v1/lockdown',
          { scope: 'building', reason: modal.reason.trim() },
          {
            skipTenantScope: true,
            headers: { 'Idempotency-Key': modal.idempotencyKey },
          },
        );
      } else {
        res = await apiClient.post<LockdownResponse>(
          '/api/v1/lockdown/clear',
          { reason: modal.reason.trim() || 'clear' },
          {
            skipTenantScope: true,
            headers: { 'Idempotency-Key': modal.idempotencyKey },
          },
        );
      }
      const kind = modal.kind;
      setModal(null);
      // UX-006: the endpoint returns { unifi_request_id } — proof the
      // controller accepted the command, not that any door moved. This page
      // says so itself two cards up ("Current state (inferred)"), so the toast
      // must not claim a physical outcome it cannot observe.
      const requestId = res?.data?.unifi_request_id?.trim() ?? '';
      const ref = requestId ? ` Request ${requestId}.` : '';
      setToast(
        kind === 'engage'
          ? `Lockdown command accepted by the controller.${ref} Doors report as locked once the controller applies it — this page does not poll for confirmation.`
          : `Clear command accepted by the controller.${ref} Normal enforcement resumes once the controller applies it.`,
      );
    } catch (err: unknown) {
      const apiErr = err instanceof ApiError ? err : null;
      const notLiveVerified =
        apiErr !== null
        && apiErr.status === 422
        && typeof apiErr.details === 'object'
        && apiErr.details !== null
        && (apiErr.details as { reason?: string }).reason === 'lockdown_not_live_verified';
      setModal({
        ...modal,
        busy: false,
        error: {
          message: apiErr ? apiErr.message : 'Could not reach the controller.',
          notLiveVerified,
        },
      });
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <span className="rounded-full bg-error-container p-2 text-on-error-container">
            <Icon name="lock" />
          </span>
          <div>
            <h1 className="font-headline-sm text-headline-sm text-on-surface">
              Global emergency lockdown
            </h1>
            <p className="font-body-md text-body-md text-on-surface-variant">
              Site-wide door lock control. Engaging will lock every door on the
              controller until cleared. Use only in true emergencies.
            </p>
          </div>
        </div>
      </header>

      <Card>
        <CardBody>
          <h2 className="font-title-md text-title-md text-on-surface mb-1">
            Heads-up
          </h2>
          {/* UX-007: this card used to tell operators the fix was to flip a
              row in the compatibility matrix. That row has been verified since
              2026-05-20 and flipping a markdown table changes nothing at
              runtime. The actual gate is an environment variable read by
              apps/api/src/routes/lockdown.ts. Naming the wrong lever on the
              emergency page is the worst place to be approximately right. */}
          <p className="font-body-md text-body-md text-on-surface-variant">
            On production installs the backend refuses both engage and clear
            with a <code>lockdown_not_live_verified</code> error unless{' '}
            <code>ALLOW_UNVERIFIED_LOCKDOWN</code> is set in the API
            environment. Emergency controls were live-verified against a
            UDM-Pro-SE on 9.4.34, so if the buttons below fail with that
            message the remaining step is a server configuration change — made
            by whoever administers this deployment — not a retry from this
            page.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardBody>
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="font-title-md text-title-md text-on-surface">
                Current state (inferred)
              </h2>
              <p className="font-body-md text-body-md text-on-surface-variant">
                Derived from the most recent <code>LOCKDOWN_ENGAGED</code> /{' '}
                <code>LOCKDOWN_CLEARED</code> audit entry. The portal does not
                yet poll the controller for ground truth.
              </p>
            </div>
            {inferredState === 'engaged' && (
              <Badge tone="danger">LOCKED — engaged</Badge>
            )}
            {inferredState === 'cleared' && <Badge tone="success">Normal</Badge>}
            {inferredState === 'unknown' && <Badge>Unknown</Badge>}
          </div>
        </CardBody>
      </Card>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardBody>
            <h2 className="font-title-md text-title-md text-on-surface mb-2">
              Engage lockdown
            </h2>
            <p className="font-body-md text-body-md text-on-surface-variant mb-3">
              Lock every door on the controller. All access groups, schedules,
              and credentials are overridden until you explicitly clear.
            </p>
            <Button variant="danger" onClick={() => openModal('engage')} data-testid="engage-btn">
              <Icon name="lock" className="text-sm" />
              <span className="ml-1">Engage global lockdown</span>
            </Button>
          </CardBody>
        </Card>

        <Card>
          <CardBody>
            <h2 className="font-title-md text-title-md text-on-surface mb-2">
              Clear lockdown
            </h2>
            <p className="font-body-md text-body-md text-on-surface-variant mb-3">
              Restore normal access enforcement. Access Zones and schedules
              resume controlling who gets in.
            </p>
            <Button variant="secondary" onClick={() => openModal('clear')} data-testid="clear-btn">
              <Icon name="lock_open" className="text-sm" />
              <span className="ml-1">Clear global lockdown</span>
            </Button>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardBody>
          <h2 className="font-title-md text-title-md text-on-surface mb-2">
            Recent lockdown audit
          </h2>
          {recent === null && (
            <p className="font-body-md text-body-md text-on-surface-variant">
              Loading…
            </p>
          )}
          {recentError && (
            <p className="font-body-md text-body-md text-error" role="alert">
              {recentError} The current state above is shown as Unknown because
              this history is what it is inferred from.
            </p>
          )}
          {!recentError && recent !== null && recent.length === 0 && (
            <p className="font-body-md text-body-md text-on-surface-variant">
              No lockdown events recorded on this install.
            </p>
          )}
          {recent !== null && recent.length > 0 && (
            <ul className="space-y-2 font-body-md text-body-md">
              {recent.map((r) => (
                <li key={r.id} className="flex items-center gap-3">
                  <Badge tone={r.action === 'LOCKDOWN_ENGAGED' ? 'danger' : 'success'}>
                    {r.action === 'LOCKDOWN_ENGAGED' ? 'Engaged' : 'Cleared'}
                  </Badge>
                  <span className="text-on-surface">
                    {new Date(r.created_at).toLocaleString()}
                  </span>
                  <Link
                    to={`/activity/${r.id}`}
                    className="text-primary hover:underline font-label-md text-label-md"
                  >
                    open entry →
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {/* UX-022: this was a bare <div> — no role, no aria-modal, no accessible
          name, no focus moved in, no focus trap, no Escape, no focus restored.
          It was the least accessible dialog in the product and it is the one
          an operator reaches for during an emergency, quite possibly from the
          keyboard. It now uses the same shell as every other dialog.

          Not dismissible: a stray Escape or backdrop click must not discard a
          half-typed CONFIRM on a site-wide door command. Cancel is explicit. */}
      {modal && (
        <Dialog
          title={modal.kind === 'engage' ? 'Engage global lockdown?' : 'Clear global lockdown?'}
          onClose={() => setModal(null)}
          dismissible={false}
          initialFocus="#lockdown-reason"
          className="bg-surface-container-high"
        >
          <>
            <p className="font-body-md text-body-md text-on-surface">
              {modal.kind === 'engage' ? (
                <>
                  This locks <strong>every door on the controller</strong>{' '}
                  immediately. Schedules and access groups will not unlock
                  doors until you clear. This action is audited with your
                  reason and the controller request id.
                </>
              ) : (
                <>
                  This restores normal access enforcement site-wide.
                  Operators should confirm the emergency is resolved before
                  clearing.
                </>
              )}
            </p>
            <form onSubmit={submitModal} className="mt-4 space-y-3">
              <div>
                <Label htmlFor="lockdown-reason">
                  Reason {modal.kind === 'clear' ? '(optional)' : ''}
                </Label>
                <Input
                  id="lockdown-reason"
                  required={modal.kind === 'engage'}
                  maxLength={500}
                  value={modal.reason}
                  onChange={(e) => setModal({ ...modal, reason: e.target.value })}
                  placeholder={
                    modal.kind === 'engage'
                      ? 'e.g. Fire alarm activated 3F'
                      : 'e.g. All clear from fire dept'
                  }
                  data-testid="lockdown-reason"
                />
              </div>
              <div>
                <Label htmlFor="lockdown-confirm">
                  Type <code>{CONFIRM_TEXT[modal.kind]}</code> to confirm
                </Label>
                <Input
                  id="lockdown-confirm"
                  required
                  value={modal.confirmText}
                  onChange={(e) =>
                    setModal({ ...modal, confirmText: e.target.value })
                  }
                  autoComplete="off"
                  data-testid="lockdown-confirm"
                />
              </div>
              {modal.error && (
                <div
                  role="alert"
                  className="rounded bg-error-container px-3 py-2 font-body-md text-body-md text-on-error-container"
                >
                  {modal.error.notLiveVerified ? (
                    <>
                      <strong>Lockdown is disabled on this install.</strong>{' '}
                      The API refuses global lockdown in production unless{' '}
                      <code>ALLOW_UNVERIFIED_LOCKDOWN</code> is set in its
                      environment. Retrying will not help — this needs a server
                      configuration change by whoever administers the
                      deployment.
                    </>
                  ) : (
                    modal.error.message
                  )}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setModal(null)}
                  disabled={modal.busy}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant={modal.kind === 'engage' ? 'danger' : 'primary'}
                  disabled={
                    modal.busy
                    || modal.confirmText !== CONFIRM_TEXT[modal.kind]
                  }
                  data-testid="lockdown-confirm-btn"
                >
                  {modal.busy
                    ? modal.kind === 'engage'
                      ? 'Locking…'
                      : 'Clearing…'
                    : modal.kind === 'engage'
                      ? 'Engage lockdown'
                      : 'Clear lockdown'}
                </Button>
              </div>
            </form>
          </>
        </Dialog>
      )}

      {toast && (
        <div
          role="status"
          className="fixed bottom-4 right-4 rounded-md bg-inverse-surface px-4 py-2 font-body-md text-body-md text-inverse-on-surface shadow-lg"
        >
          {toast}
          <button
            type="button"
            onClick={() => setToast(null)}
            className="ml-3 underline"
            aria-label="Dismiss"
          >
            close
          </button>
        </div>
      )}
    </div>
  );
}
